import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  Collapse,
  HStack,
  AccordionProps,
  AccordionItemProps,
  AccordionButtonProps,
  IconProps,
  Text,
} from "@chakra-ui/react";
import React, { ReactNode, useState, ComponentType } from "react";
import { Except } from "type-fest";

type CollapsibleSectionProps = {
  icon?: ComponentType<IconProps>;
  title: string;
  children: ReactNode;
  defaultIsOpen?: boolean;
  accordionItemProps?: Except<AccordionItemProps, "overflow">;
  accordionButtonProps?: AccordionButtonProps;
  accordionProps?: Except<
    AccordionProps,
    "onChange" | "allowToggle" | "defaultIndex" | "index"
  >;
};

/**
 * A collapsible section.
 *
 * Internally, this uses an Accordion with a single AccordionItem, but it does
 * not use the AccordionPanel.
 *
 * The default AccordionPanel uses a Collapse, which is styled as
 * overflow:hidden, which clips child popovers (unless the popover is portalled,
 * which we prefer to avoid doing due to zIndex issues). This implementation
 * also uses a Collapse, but the overflow is set to visible once it is open to
 * remove the clipping issue.
 */
export function CollapsibleSection({
  icon,
  title,
  children,
  defaultIsOpen = false,
  accordionItemProps,
  accordionButtonProps,
  accordionProps,
}: CollapsibleSectionProps): React.ReactElement {
  // The Collapse relies on overflow:hidden to do the animation, but once open
  // we need overflow:visible, so the date picker popover does not get clipped.
  const [isOverflowVisible, setIsOverflowVisible] = useState(defaultIsOpen);

  const Icon = icon;

  return (
    <Accordion
      allowToggle
      defaultIndex={defaultIsOpen ? 0 : undefined}
      onChange={() => {
        setIsOverflowVisible(false);
      }}
      {...accordionProps}
    >
      <AccordionItem
        borderRadius="md"
        borderWidth="1px"
        borderColor="gray.300"
        backgroundColor="gray.25"
        {...accordionItemProps}
      >
        {({ isExpanded }) => (
          <>
            <AccordionButton
              borderTopRadius="md"
              borderBottomRadius={isExpanded ? "none" : "md"}
              {...accordionButtonProps}
            >
              <HStack spacing={2} flex={1}>
                {Icon && <Icon flexShrink={0} />}
                <Text flex={1} textAlign="left">
                  {title}
                </Text>
              </HStack>
              <AccordionIcon flexShrink={0} />
            </AccordionButton>

            <Collapse
              in={isExpanded}
              animateOpacity
              onAnimationComplete={() => {
                if (isExpanded) {
                  setIsOverflowVisible(true);
                }
              }}
              style={{
                overflow: isOverflowVisible ? "visible" : "hidden",
              }}
            >
              {children}
            </Collapse>
          </>
        )}
      </AccordionItem>
    </Accordion>
  );
}
